import { eventChannel } from 'redux-saga';
import { getAppUrl } from '../utils/app-url';
import { pushToQueue, sendQueue } from './offline-queue';
import { shuffleArray, shuffleConnections } from '../utils/seeded-random';
import { delay } from '../../utils/delay';
import { fetchConnections } from './connect';
import { CLIENT_WEBSOCKET_AUTH_ERROR } from '../../../../src/actions/system-actions';
import { store } from '../../index';

import { debugEvent } from '../../actions/debug';

import {
  tryingToEstablishWebsocketConnection,
  websocketConnectionEstablished,
  subsequentSocketConnectRetries,
  exceededSocketConnectRetries,
  subsecquentFetchConnectionsRetries
} from '../actions/from-client-frontend';

import { getJwtBodyPart } from '../utils/jwt';

import messageDeliveryMonitor from '../../../../src/shared/transport/delivery-monitor';
import { EVENT_DELIVERY_PATHS } from '../../../../src/shared/transport/delivery-monitor-event-paths';

const messageDeliveryMonitorInstance = messageDeliveryMonitor({ name: 'clients-frontend', evTimeout: 2500 });

export const WS_STATUS = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

export const WEBSOCKET_CLOSED_EVENT = 'websocket/disconnected';
export const WEBSOCKET_ERROR_OCCURED = 'websocket/error-occured';

export const RETRY_TIMEOUT_MS = 1500;
export const INITIAL_RECONNECTS_LEFT = 1000;

export const SOCKET_RECONNECT_LIMITS_FOR_DEBUG_LOG = 980;
export const FETCH_CONNECTIONS_LIMIT_FOR_DEBUG_LOG = 20;

export const WEBSOCKET_CONNECTION_FAILED_EVENTS = [
  WEBSOCKET_CLOSED_EVENT,
  WEBSOCKET_ERROR_OCCURED
];

let wsInstance;
let heartbeatTimeout;
let reconnectsLeft = INITIAL_RECONNECTS_LEFT;

const wsSend = (ws, stringifiedJson) => {
  try {
    ws.send(stringifiedJson);
  } catch (e) {
    console.log('Failed to send to ws');

    store.dispatch(
      debugEvent({
        name: 'FAILED_TO_SEND_VIA_WS',
        payload: {
          stringifiedJson
        }
      })
    );
  }
};

export const sendToCluster = (msg, nolimit = false) => {
  const socketReadyState = wsInstance && wsInstance.readyState;

  if (socketReadyState === WS_STATUS.OPEN) {
    sendQueue({ wsInstance });

    const json = JSON.stringify(
      messageDeliveryMonitorInstance.packMessage(msg)
    );

    if (json.length < 10 * 1024 || nolimit) {
      wsSend(wsInstance, json);

      messageDeliveryMonitorInstance.waitFor({
        stringifiedJsonMessage: json,
        path: EVENT_DELIVERY_PATHS.CLIENT_TO_WORKER,
        onTimeout: (debugString) =>
          store.dispatch(
            debugEvent({
              name: 'MESSAGE_SENT_NO_CONFIRMATION_RECEIVED',
              payload: {
                msg,
                debugString
              }
            })
          )
      });
    } else {
      store.dispatch(
        debugEvent({
          name: 'TOO_BIG_JSON_TO_SEND_TO_SOCKET',
          payload: {
            size: json.length
          }
        })
      );
    }

  } else {
    store.dispatch(
      debugEvent({
        name: 'ATTEMPT_TO_WS_SEND_WHILE_SOCKET_IS_NOT_IN_READY_STATE',
        payload: {
          socketReadyState,
          msg
        }
      })
    );

    pushToQueue(msg);
  }
};

export const loadConnections = async ({ IMPULSE_URLS = [], clientToken }) => {
  let connections = null;
  let retriesCount = 0;

  while (!connections) {

    if (retriesCount > FETCH_CONNECTIONS_LIMIT_FOR_DEBUG_LOG) {
      store.dispatch(
        subsecquentFetchConnectionsRetries({ count: retriesCount })
      );
    }

    try {
      const shuffledImpulseUrls = shuffleArray(IMPULSE_URLS);
      const response = await fetchConnections({ wsApi: shuffledImpulseUrls[0], clientToken });

      if (!response || !response.connections) {
        throw 'Cannot fetch connections';
      }

      connections = response.connections;
    } catch (e) {
      console.error('Failed to fetch connections', e, 'Retry...');

      store.dispatch(
        debugEvent({
          name: 'FAILED_TO_FETCH_CONNECTIONS',
          payload: {
            clientToken: getJwtBodyPart({ token: clientToken })
          }
        })
      );

      // await delay(RETRY_TIMEOUT_MS * retriesCount);
      await delay(3000);
    }

    retriesCount = retriesCount + 1;
  }

  return connections;
};

export const initWebsocket = ({
  IMPULSE_URLS,
  token = null,
  username = null,
  visitorToken = null,
  unauthorized = false
}) => {
  return eventChannel((emitter) => {
    const appUrl = getAppUrl();

    const wsConection = async () => {
      store.dispatch(tryingToEstablishWebsocketConnection());

      if (reconnectsLeft <= 0) {
        console.error('Too many impulse reconnects');

        store.dispatch(
          exceededSocketConnectRetries({ count: INITIAL_RECONNECTS_LEFT })
        );

        store.dispatch(
          debugEvent({
            name: 'TOO_MANY_IMPULSE_RECONNECTS',
            payload: {
              reconnectsLeft,
              INITIAL_RECONNECTS_LEFT
            }
          })
        );

        return;
      }

      const connections = await loadConnections({ IMPULSE_URLS, clientToken: token });
      const [connection, ...other] = shuffleConnections(connections);

      const portAndHostnameToConnect =
        connection &&
        connection.ports &&
        connection.ports[0] &&
        Boolean(Number(connection.ports[0])) &&
        connection.hostname;

      if (!portAndHostnameToConnect) {
        store.dispatch(
          debugEvent({
            name: 'NO_PORTS_AND_HOSTNAME_TO_CONNECT',
            payload: {
              connection
            }
          })
        );

        await delay(RETRY_TIMEOUT_MS * 3);
        return wsConection();
      }

      const {
        hostname,
        ports,
        IMPULSE_URL_PORT_MAPPING_IN_USE = false,
        ENABLED_DELIVERY_MONITOR_EVENT_PATHS = {}
      } = connection;

      messageDeliveryMonitorInstance.setMonitoredEventPaths({ paths: ENABLED_DELIVERY_MONITOR_EVENT_PATHS });

      const [port, ...otherPorts] = ports;
      const visitorTokenGetParam = visitorToken ? `visitor_token=${visitorToken}` : '';

      reconnectsLeft--;

      if (reconnectsLeft < SOCKET_RECONNECT_LIMITS_FOR_DEBUG_LOG) {
        store.dispatch(
          subsequentSocketConnectRetries({ reconnectsLeft })
        );
      }

      store.dispatch(
        debugEvent({
          name: 'OPENING_SOCKET',
          payload: {
            clientToken: getJwtBodyPart({ token }),
            clientTokenEncoded: getJwtBodyPart({ token: encodeURI(token) }),
            visitorToken: getJwtBodyPart({ token: visitorToken })
          }
        })
      );

      const portString = IMPULSE_URL_PORT_MAPPING_IN_USE ? `/p${port}` : `:${port}`;
      const clientTokenGetParam = token ? `token=${encodeURI(token)}` : '';

      wsInstance = new WebSocket(
        `wss://${hostname}${portString}?${clientTokenGetParam}&app=${appUrl}` +
        `&ua=${encodeURI(navigator.userAgent.substring(0,200))}`+
        `&username=${username}`+
        `&${visitorTokenGetParam}` +
        `&unauthorized=${Number(unauthorized)}`
      );

      wsInstance.onopen = () => {
        store.dispatch(websocketConnectionEstablished());

        store.dispatch(
          debugEvent({
            name: 'SOCKET_OPENED'
          })
        );

        sendQueue({ wsInstance });
      };

      wsInstance.onerror = (ev) => {
        console.error('WebSocket error ', ev);

        store.dispatch(
          debugEvent({
            name: 'SOCKET_ERROR'
          })
        );

        emitter(WEBSOCKET_ERROR_OCCURED);
      };

      wsInstance.onclose = async (error) => {
        console.warn('Socket closed. Retrying connect...');

        store.dispatch(
          debugEvent({
            name: 'SOCKET_CLOSED',
            payload: {
              code: error && error.code
            }
          })
        );

        await delay(RETRY_TIMEOUT_MS);
        emitter(WEBSOCKET_CLOSED_EVENT);
      };

      wsInstance.onmessage = async (e) => {
        if (heartbeatTimeout) {
          clearInterval(heartbeatTimeout);
        }

        heartbeatTimeout = setTimeout(() => {
          console.warn('Heartbeat timeout. Closing WebSocket...');

          store.dispatch(
            debugEvent({
              name: 'HB_TIMEOUT_CLOSING_SOCKET'
            })
          );

          wsInstance.close();
        }, 12000);

        let action;

        try {
          action = JSON.parse(e.data);

          messageDeliveryMonitorInstance.packetReceived({
            from: 'impulse-worker',
            message: action,
            path: EVENT_DELIVERY_PATHS.WORKER_TO_CLIENT,
            pong: (data) => wsSend(wsInstance, data)
          });

          const errorOccured = Boolean(
            Boolean(action && action.type === CLIENT_WEBSOCKET_AUTH_ERROR) ||
            Boolean(WEBSOCKET_CONNECTION_FAILED_EVENTS.includes(action))
          );

          if (errorOccured) {
            store.dispatch(
              debugEvent({
                name: 'ERROR_RECEIVED_ON_SOCKET',
                payload: {
                  action
                }
              })
            );
          }

          if (!errorOccured) {
            reconnectsLeft = INITIAL_RECONNECTS_LEFT;
          }
        } catch (e) {
          store.dispatch(
            debugEvent({
              name: 'ERROR_PARSING_WS_EVENT',
              payload: {
                ev: e.toString()
              }
            })
          );

          return console.error(`Error parsing WS event ${e.toString()}`);
        }

        return emitter(action);
      };
    };

    wsConection();

    // unsubscribe
    return () => {
      console.warn('CLOSING WEBSOCKET');

      store.dispatch(
        debugEvent({
          name: 'CLOSING_WEBSOCKET (channel unsubscribed)'
        })
      );

      wsInstance &&
      typeof wsInstance.close === 'function' &&
      wsInstance.close();
    };
  });
};
